import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useSite } from "@/context/SiteContext";
import { usePageContent } from "@/context/PageContentContext";
import { buildConsultancyMessage, openWhatsApp } from "@/lib/whatsappMessages";

const BUSINESS_TYPES = [
  "Restaurant", "Cloud Kitchen", "Hotel", "Cafe",
  "Caterer", "QSR", "Distributor", "Manufacturer", "Franchise Chain",
];

export default function FoodConsultancy() {
  const { content: site } = useSite();
  const { content } = usePageContent("consultancy");
  const [form, setForm] = useState({
    company_name: "", contact_person: "", mobile: "", email: "",
    business_type: BUSINESS_TYPES[0], city: "", requirement: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/leads/consultancy", form);
      openWhatsApp(site.whatsapp, buildConsultancyMessage(form));
      setStatus({
        state: "success",
        msg: "Lead saved. WhatsApp opened — finish the conversation there.",
      });
      setForm({ company_name: "", contact_person: "", mobile: "", email: "",
        business_type: BUSINESS_TYPES[0], city: "", requirement: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <div data-testid="page-food-consultancy" className="bg-cream">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[var(--k-sand)] opacity-50 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="font-mono-label text-[var(--k-green)] mb-5">{content.hero_eyebrow}</div>
            <h1 data-testid="fc-headline" className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]">
              {content.hero_title_pre} <em className="green-gradient-text not-italic">{content.hero_title_em}</em> {content.hero_title_post}
            </h1>
            <p className="mt-7 text-lg text-[var(--k-text-2)] max-w-2xl leading-relaxed">
              {content.hero_intro}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-beige" data-testid="fc-services">
        <div className="container-k">
          <div className="max-w-3xl mb-14">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.services_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.services_title_pre} <em className="green-gradient-text not-italic">{content.services_title_em}</em>{content.services_title_post}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(content.services || []).map((s, i) => (
              <motion.div
                key={s + i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="organic-card p-7 flex items-center gap-4"
                data-testid={`fc-service-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[var(--k-bg-2)] flex items-center justify-center border border-[var(--k-line)] shrink-0">
                  <span className="font-mono-label text-[var(--k-green)] text-[0.55rem]">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-serif-display text-xl text-[var(--k-text)] leading-tight">{s}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream" data-testid="fc-form-section">
        <div className="container-k grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.form_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.form_title_pre} <em className="green-gradient-text not-italic">{content.form_title_em}</em>{content.form_title_post}
            </h2>
            <p className="mt-6 text-[var(--k-text-2)] leading-relaxed max-w-md">
              {content.form_intro}
            </p>
          </div>

          <form onSubmit={submit} className="organic-card p-8 md:p-12 lg:col-span-7" data-testid="fc-form">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Company Name *">
                <input className="kostra-input" required value={form.company_name} onChange={update("company_name")} data-testid="fc-input-company" />
              </Field>
              <Field label="Contact Person *">
                <input className="kostra-input" required value={form.contact_person} onChange={update("contact_person")} data-testid="fc-input-contact" />
              </Field>
              <Field label="Mobile *">
                <input className="kostra-input" type="tel" required value={form.mobile} onChange={update("mobile")} data-testid="fc-input-mobile" placeholder="+91 99999 00000" />
              </Field>
              <Field label="Email *">
                <input className="kostra-input" type="email" required value={form.email} onChange={update("email")} data-testid="fc-input-email" />
              </Field>
              <Field label="Business Type">
                <select className="kostra-input cursor-pointer" value={form.business_type} onChange={update("business_type")} data-testid="fc-select-business" style={{ background: "var(--k-card)" }}>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="City">
                <input className="kostra-input" value={form.city} onChange={update("city")} data-testid="fc-input-city" />
              </Field>
              <Field label="Requirement *" full>
                <textarea className="kostra-input resize-none" rows={4} required value={form.requirement} onChange={update("requirement")} data-testid="fc-input-requirement" placeholder="Outlets, locations, scale, timelines, budget…" />
              </Field>
            </div>

            {status.state === "success" && <StatusBanner ok msg={status.msg} testId="fc-form-success" />}
            {status.state === "error" && <StatusBanner msg={status.msg} testId="fc-form-error" />}

            <button type="submit" disabled={status.state === "loading"} className="btn-green mt-10" data-testid="fc-submit-btn">
              {status.state === "loading" ? "Submitting…" : (<>Send to WhatsApp <MessageCircle className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}

function StatusBanner({ ok, msg, testId }) {
  return (
    <div data-testid={testId} className={`mt-8 flex items-start gap-3 p-4 rounded ${ok ? "border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)]" : "border border-red-300 bg-red-50"}`}>
      {ok ? <CheckCircle2 className="w-5 h-5 text-[var(--k-green)] mt-0.5" /> : <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />}
      <p className="text-[var(--k-text)] text-sm">{msg}</p>
    </div>
  );
}
